/**
 * Registro de templates.
 *
 * `tests/invariantes/` ITERA este registro: cada template novo herda o portão
 * de qualidade inteiro (determinismo, as 9 regras de distrator, orçamento de
 * rejeição, sanidade das figuras) sem escrever um único teste. É o que torna a
 * autoria de itens barata — e a autoria, não o motor, é o grosso do trabalho daqui
 * em diante.
 */

import type { ItemTemplate, TemplateId } from '../schema/item.ts';
import { reajustesSucessivos } from '../items/h2-04-07-reajustes/template.ts';
import { funcaoGrafico } from '../items/h1-08-01-funcao-grafico/template.ts';
import { mediaAgrupada } from '../items/h3-30-05-media-agrupada/template.ts';
import { velocidadeMedia } from '../items/h3-25-07-velocidade-media/template.ts';
import { diagonaisPoligono } from '../items/h2-20-09-diagonais/template.ts';
import { pontosNaMalha } from '../items/h3-07-K01-malha/template.ts';
import { bissetriz } from '../items/k-bissetriz/template.ts';
import { trapezioRetangulo } from '../items/h1-15-K02-trapezio/template.ts';
import { verticesDoQuadrado } from '../items/h3-11-K01-quadrado/template.ts';
import { simetriaNaMalha } from '../items/h3-18-K01-simetria-malha/template.ts';
import { simetriaArrastar } from '../items/h3-18-K02-simetria-arrastar/template.ts';
import { simetriaConstrucao } from '../items/h3-20-K01-simetria-construcao/template.ts';
import {
  graficoProgressao,
  graficoTendencia,
} from '../items/h3-30-K01-grafico-seletor/template.ts';
import {
  anguloExterno,
  anguloInterno,
  somaAngulosExternos,
  somaAngulosInternos,
} from '../items/h2-13-angulos/templates.ts';
import { quadradoEscolha } from '../items/h3-11-K02-quadrado-escolha/template.ts';
import {
  erroAngulos,
  erroEquacao,
  erroReajustes,
  erroVelocidade,
} from '../items/k-encontre-erro/templates.ts';
import { retaNumerica } from '../items/h1-01-K01-reta-numerica/template.ts';
import { ordenarReais } from '../items/h1-01-K02-ordenar/template.ts';
import { expressaoDaTabela } from '../items/h1-12-K02-expressao/template.ts';
import { ajustarReta } from '../items/h1-08-K02-ajustar-reta/template.ts';
import { dadosEvento } from '../items/h1-30-K01-dados-evento/template.ts';
import { planificacoesCubo } from '../items/h3-17-K01-planificacoes-cubo/template.ts';
import { gangorraMedia } from '../items/h1-36-K01-gangorra/template.ts';
import { ampliacao } from '../items/h1-22-K01-ampliacao/template.ts';
import { escalaMapa } from '../items/h1-24-K01-escala-mapa/template.ts';
import { eixoTruncado } from '../items/h1-37-K01-eixo-truncado/template.ts';
import { mediaAlvo } from '../items/h3-33-K01-media-alvo/template.ts';
import { setorAngulo } from '../items/h1-34-K01-setor-angulo/template.ts';
import { balancaEquacao } from '../items/h1-10-K01-balanca/template.ts';
import { ladrilhar } from '../items/h2-15-K01-ladrilhar/template.ts';
import { urnaChance } from '../items/h1-32-K01-urna/template.ts';
import { montarColunas } from '../items/h3-30-K02-montar-colunas/template.ts';
import { quadradoCoordenadas } from '../items/h3-11-K03-quadrado-coordenadas/template.ts';
import { caixaPlanificacao } from '../items/h1-19-K01-caixa-planificacao/template.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyTemplate = ItemTemplate<any, any>;

/**
 * Em ordem de nível, para o laboratório listar do mais simples ao mais elaborado.
 *
 * As seis competências da matriz CAEd estão cobertas: C1 (reajustes), C2 (função e
 * malha), C3 (bissetriz e trapézio), C4 (velocidade), C5 (diagonais), C6 (média).
 */
export const REGISTRY: readonly AnyTemplate[] = [
  reajustesSucessivos, //  N0 · C1 · resposta numérica digitada
  erroEquacao, //          N0 · C1 · encontre o erro: equação
  erroReajustes, //        N0 · C1 · encontre o erro: porcentagens
  erroVelocidade, //       N0 · C4 · encontre o erro: velocidade média
  erroAngulos, //          N0 · C3 · encontre o erro: ângulos internos
  funcaoGrafico, //        N1 · C2 · alternativas gráficas (E6)
  mediaAgrupada, //        N1 · C6 · figura gerada por parâmetros
  setorAngulo, //          N1 · C6 · ler um gráfico de setores pelo ângulo do setor
  quadradoEscolha, //      N1 · C2 · alternativas = pares de coordenadas
  expressaoDaTabela, //    N1 · C2 · expressão digitada, corrigida por equivalência
  quadradoCoordenadas, //  N1 · C2 · ficha com campos de coordenadas
  velocidadeMedia, //      N2 · C4 · estímulo animado
  diagonaisPoligono, //    N2 · C5 · estímulo manipulável
  somaAngulosInternos, //  N2 · C3 · explorador: triângulos das diagonais
  anguloInterno, //        N2 · C3 · explorador: polígonos regulares
  somaAngulosExternos, //  N2 · C3 · animação: três polígonos, a mesma volta
  anguloExterno, //        N2 · C3 · animação + giro em cada vértice
  caixaPlanificacao, //    N2 · C3 · seletor que planifica a caixa sobre a malha
  escalaMapa, //           N2 · C4 · régua sobre o mapa, escala gráfica ou numérica
  eixoTruncado, //         N2 · C6 · julgar uma manchete movendo o início do eixo
  pontosNaMalha, //        N3 · C2 · marcação na malha de inteiros
  verticesDoQuadrado, //   N3 · C2 · marcação com resposta às vezes não única
  retaNumerica, //         N3 · C1 · marcadores arrastados sobre a reta
  ordenarReais, //         N3 · C1 · cartões postos em ordem
  dadosEvento, //          N3 · C5 · casas marcadas no espaço amostral de dois dados
  planificacoesCubo, //    N3 · C3 · figuras marcadas: quais fecham o cubo
  montarColunas, //        N3 · C6 · colunas levantadas a partir da tabela
  mediaAlvo, //            N3 · C6 · eixo III: mexer numa coluna até a média ser a pedida
  gangorraMedia, //        N3 · C6 · o apoio da régua no ponto de equilíbrio
  urnaChance, //           N3 · C5 · eixo III: acrescentar bolas até a chance ser a pedida
  ladrilhar, //            N3 · C3 · eixo III: quais polígonos pavimentam o plano
  ampliacao, //            N3 · C4 · arrastar a cópia até a razão pedida
  simetriaArrastar, //     N3 · C3 · arrastar os vértices até o simétrico
  simetriaNaMalha, //      N3 · C3 · reflexão contando quadradinhos
  graficoTendencia, //     N3 · C6 · resposta pelo seletor, faixa de valores certos
  graficoProgressao, //    N3 · C2 · resposta pelo seletor, variação constante
  ajustarReta, //          N3 · C2 · dois controles até a reta passar pelos pontos
  balancaEquacao, //       N4 · C2 · a balança de dois pratos: resolver mexendo nos pratos
  bissetriz, //            N4 · C3 · construção com régua e compasso
  trapezioRetangulo, //    N4 · C3 · construção com resposta não única
  simetriaConstrucao, //   N4 · C3 · reflexão sem a ferramenta de refletir
];

const byId = new Map<string, AnyTemplate>(REGISTRY.map((t) => [t.id as string, t]));

export function getTemplate(id: TemplateId | string): AnyTemplate {
  const t = byId.get(id as string);
  if (!t) throw new Error(`template não registrado: ${String(id)}`);
  return t;
}

export function hasTemplate(id: string): boolean {
  return byId.has(id);
}
